// Phase 9 /run/jobs — Phase 5 long-running jobs (LHS, sensitivity, optimisation).
//
// Features
//   • Sortable table of jobs with status pill, kind, child count, progress.
//   • Row click opens a drawer showing the children list + result_summary.
//   • Cancel button on running jobs.
//   • "New Job" button exposes a kind-picker placeholder.
import { useEffect, useMemo, useState } from "react";
import { Dice5, X } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { jobs as jobsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

const KINDS = [
    { id: "monte_carlo", label: "Monte Carlo" },
    { id: "lhs", label: "LHS sweep" },
    { id: "sensitivity", label: "Sensitivity" },
    { id: "optimisation", label: "Optimisation" },
];

function statusPillClass(s) {
    switch (s) {
        case "succeeded":
            return "bg-ux-success/20 text-ux-success border-ux-success/40";
        case "running":
            return "bg-ux-accent/20 text-ux-accent border-ux-accent/40";
        case "queued":
            return "bg-ux-warning-soft text-ux-warning border-ux-warning/40";
        case "cancelled":
            return "bg-ux-bg-2 text-ux-text-3 border-ux-border-2";
        case "failed":
            return "bg-ux-danger/20 text-ux-danger border-ux-danger/40";
        default:
            return "bg-ux-bg-2 text-ux-text-3 border-ux-border-2";
    }
}

export default function JobsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [open, setOpen] = useState(null); // { id, children, job }
    const [showNew, setShowNew] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const data = await jobsApi.list();
            setRows(Array.isArray(data) ? data : data?.items || []);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e, "Failed to load jobs"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 5000);
        return () => clearInterval(id);
    }, []);

    const openDrawer = async (j) => {
        try {
            const children = await jobsApi.children(j.id || j.job_id);
            setOpen({ id: j.id || j.job_id, children: children?.items || children || [], job: j });
        } catch (e) {
            toast.error(formatApiError(e, "Could not load children"));
        }
    };

    const cancel = async (j) => {
        if (!window.confirm(`Cancel job ${j.id || j.job_id}?`)) return;
        try {
            await jobsApi.cancel(j.id || j.job_id);
            toast.success("Job cancelled");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Cancel failed"));
        }
    };

    return (
        <div data-testid="jobs-page">
            <PageHeader
                title="Jobs"
                subtitle="Long-running batch jobs — Monte Carlo, LHS, sensitivity, optimisation."
                actions={[
                    <button
                        key="new"
                        onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] bg-ux-accent text-black hover:opacity-90"
                        data-testid="jobs-new-button"
                    >
                        <Dice5 className="w-4 h-4" /> New Job
                    </button>,
                ]}
            />

            {err && (
                <div
                    className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]"
                    data-testid="jobs-error"
                >
                    {err}
                </div>
            )}

            <div className="rounded-lg border border-ux-border-1 overflow-hidden">
                <table className="w-full text-[13px]" data-testid="jobs-table">
                    <thead className="bg-ux-bg-1 text-ux-text-3 text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-3 py-2">ID</th>
                            <th className="text-left px-3 py-2">Kind</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Progress</th>
                            <th className="text-left px-3 py-2">Children</th>
                            <th className="text-left px-3 py-2">Created</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-ux-text-3">
                                    loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-ux-text-3">
                                    No jobs yet.
                                </td>
                            </tr>
                        )}
                        {rows.map((j) => {
                            const id = j.id || j.job_id;
                            const pct = Number.isFinite(j.progress)
                                ? Math.round(j.progress * 100)
                                : 0;
                            return (
                                <tr
                                    key={id}
                                    onClick={() => openDrawer(j)}
                                    className="border-t border-ux-border-1 hover:bg-ux-bg-1 cursor-pointer"
                                    data-testid={`jobs-row-${id}`}
                                >
                                    <td className="px-3 py-2 font-mono text-ux-text-2">
                                        {id?.slice(0, 8)}…
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-1">{j.kind || "—"}</td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={[
                                                "inline-block px-2 py-0.5 rounded-full text-[11px] border",
                                                statusPillClass(j.status),
                                            ].join(" ")}
                                            data-testid={`jobs-status-${id}`}
                                        >
                                            {j.status || "—"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 w-[140px]">
                                        <div className="w-full h-1.5 bg-ux-bg-2 rounded overflow-hidden">
                                            <div
                                                className="h-full bg-ux-accent"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <div className="text-[10px] text-ux-text-3 mt-0.5 font-mono">
                                            {pct}%
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {j.child_count ?? j.children_count ?? "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-3 font-mono">
                                        {j.created_at ? new Date(j.created_at).toLocaleString() : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {j.status === "running" || j.status === "queued" ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    cancel(j);
                                                }}
                                                className="text-[12px] text-ux-danger hover:underline"
                                                data-testid={`jobs-cancel-${id}`}
                                            >
                                                cancel
                                            </button>
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Detail drawer */}
            {open && (
                <div
                    className="fixed inset-0 z-[60] flex"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setOpen(null);
                    }}
                >
                    <div className="absolute inset-0 bg-black/60" />
                    <aside
                        className="ml-auto relative h-full w-full max-w-[540px] bg-ux-bg-1 border-l border-ux-border-1 overflow-y-auto"
                        data-testid="jobs-drawer"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1 sticky top-0 bg-ux-bg-1">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                    Job
                                </div>
                                <div
                                    className="font-mono text-[13px] text-ux-text-1"
                                    data-testid="jobs-drawer-id"
                                >
                                    {open.id}
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(null)}
                                className="w-8 h-8 rounded-md hover:bg-ux-bg-2 text-ux-text-2"
                                data-testid="jobs-drawer-close"
                            >
                                <X className="w-4 h-4 mx-auto" />
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                                    Result summary
                                </div>
                                <pre
                                    className="text-[11px] font-mono bg-ux-bg-2 border border-ux-border-1 rounded p-3 max-h-[220px] overflow-auto text-ux-text-2"
                                    data-testid="jobs-drawer-summary"
                                >
                                    {open.job.result_summary
                                        ? JSON.stringify(open.job.result_summary, null, 2)
                                        : "—"}
                                </pre>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                                    Children ({open.children.length})
                                </div>
                                <ul
                                    className="space-y-1 text-[12px]"
                                    data-testid="jobs-drawer-children"
                                >
                                    {open.children.map((c, i) => (
                                        <li
                                            key={c.id || c.run_id || i}
                                            className="flex items-center justify-between gap-2 border border-ux-border-1 rounded px-2 py-1.5"
                                        >
                                            <span className="font-mono text-ux-text-2 truncate">
                                                {c.id || c.run_id}
                                            </span>
                                            <span
                                                className={[
                                                    "text-[10px] px-1.5 py-0.5 rounded-full border",
                                                    statusPillClass(c.status),
                                                ].join(" ")}
                                            >
                                                {c.status || "—"}
                                            </span>
                                        </li>
                                    ))}
                                    {open.children.length === 0 && (
                                        <li className="text-ux-text-3 italic">No children.</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            )}

            {/* New-job kind picker */}
            {showNew && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowNew(false);
                    }}
                >
                    <div
                        className="w-full max-w-md bg-ux-bg-1 rounded-lg border border-ux-border-1 p-5"
                        data-testid="jobs-new-modal"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-[16px] font-semibold text-ux-text-1">New Job</h2>
                            <button
                                onClick={() => setShowNew(false)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-[12px] text-ux-text-3 mb-3">
                            Pick a job kind to launch. Detailed job setup is driven from the
                            existing /api/jobs payload — this kind picker will wire into the
                            runs console in a later iteration.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {KINDS.map((k) => (
                                <button
                                    key={k.id}
                                    onClick={() => {
                                        setShowNew(false);
                                        toast.info(
                                            `Kind picked: ${k.label}. Job payload composer lands in v1.1.`,
                                        );
                                    }}
                                    className="px-3 py-3 rounded-md border border-ux-border-1 hover:border-ux-accent hover:bg-ux-bg-2 text-left"
                                    data-testid={`jobs-new-kind-${k.id}`}
                                >
                                    <div className="text-[13px] text-ux-text-1 font-medium">
                                        {k.label}
                                    </div>
                                    <div className="text-[11px] text-ux-text-3">{k.id}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
